import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Zap, CheckCircle2, XCircle, Database, Loader2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = {
  name: "", db_type: "mysql", host: "", port: "", username: "", password: "",
  database: "", region: "", aws_access_key_id: "", aws_secret_access_key: "",
  ssl: false, notes: "",
};

// Iter 110 — DB connection registry. Super-admin-only. Secrets are
// Fernet-encrypted server-side; the UI never receives the plaintext
// after the initial save. Password fields left blank on edit preserve
// the existing stored value.
export function DbConnectionsAdmin() {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ db_types: [] });
  const [editing, setEditing] = useState(null);   // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);   // id being tested
  const [deleting, setDeleting] = useState(null); // { id, name }

  const load = async () => {
    try {
      const [rowsR, metaR] = await Promise.all([
        api.get("/db-connections"),
        api.get("/db-connections/meta"),
      ]);
      setRows(rowsR.data);
      setMeta(metaR.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to load DB connections");
    }
  };
  useEffect(() => { load(); }, []);

  const dbTypeMeta = (t) => meta.db_types.find((x) => x.value === t) || {};
  const isDynamo = form.db_type === "dynamodb";

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (row) => {
    setForm({
      name: row.name || "",
      db_type: row.db_type || "mysql",
      host: row.host || "",
      port: row.port ?? "",
      username: row.username || "",
      password: "",                          // blank = keep existing
      database: row.database || "",
      region: row.region || "",
      aws_access_key_id: row.aws_access_key_id || "",
      aws_secret_access_key: "",              // blank = keep existing
      ssl: !!row.ssl,
      notes: row.notes || "",
      __has_password: row.has_password,
      __has_aws_secret: row.has_aws_secret,
    });
    setEditing(row.id);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        db_type: form.db_type,
        host: form.host || "",
        port: form.port === "" || form.port === null ? null : Number(form.port),
        username: form.username || "",
        // Send undefined so the field is omitted (backend keeps existing)
        // vs empty string (backend clears).
        password: form.password === "" ? (editing === "new" ? "" : undefined) : form.password,
        database: form.database || "",
        region: form.region || "",
        aws_access_key_id: form.aws_access_key_id || "",
        aws_secret_access_key: form.aws_secret_access_key === "" ? (editing === "new" ? "" : undefined) : form.aws_secret_access_key,
        ssl: !!form.ssl,
        notes: form.notes || "",
      };
      // Strip undefined so JSON body is clean.
      Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);
      if (editing === "new") {
        await api.post("/db-connections", body);
        toast.success("DB connection created");
      } else {
        await api.put(`/db-connections/${editing}`, body);
        toast.success("DB connection updated");
      }
      close();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/db-connections/${id}/test`);
      if (data.ok) toast.success("Connection succeeded");
      else toast.error(`Test failed: ${data.error || "unknown error"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Test failed");
    } finally {
      setTesting(null);
    }
  };

  const del = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/db-connections/${deleting.id}`);
      toast.success("Deleted");
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Delete failed");
    }
  };

  const onTypeChange = (t) => {
    const d = meta.db_types.find((x) => x.value === t);
    setForm((f) => ({ ...f, db_type: t, port: d?.default_port ?? f.port }));
  };

  return (
    <Card className="border-slate-200 shadow-sm p-6 space-y-4" data-testid="db-connections-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
            <Database className="h-4 w-4 text-slate-500" /> DB Connections
          </h3>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Reusable database connection strings for platform features (e.g. the Audit Log app).
            Passwords are encrypted at rest with a per-deployment key — the UI shows a masked
            indicator only. Leave the password blank on edit to keep the stored value.
          </p>
        </div>
        <Button onClick={openNew} data-testid="db-conn-new" className="gap-1.5 shrink-0">
          <Plus className="h-4 w-4" /> New
        </Button>
      </div>

      {rows === null ? (
        <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500" data-testid="db-conn-empty">
          No DB connections configured yet.
        </div>
      ) : (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 overflow-hidden">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`db-conn-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800 truncate">{r.name}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">{r.db_type_label}</span>
                  {r.has_password && <span className="text-[10px] text-emerald-600">🔒 password set</span>}
                </div>
                <div className="mt-0.5 text-xs text-slate-500 truncate">
                  {r.db_type === "dynamodb"
                    ? `region: ${r.region || "—"} · access key: ${r.aws_access_key_id ? "••••" + r.aws_access_key_id.slice(-4) : "—"}`
                    : `${r.username ? r.username + "@" : ""}${r.host || "—"}:${r.port || "—"}${r.database ? "/" + r.database : ""}`
                  }
                </div>
                {r.last_tested_at && (
                  <div className={`mt-0.5 text-[11px] flex items-center gap-1 ${r.last_test_ok ? "text-emerald-600" : "text-red-600"}`}>
                    {r.last_test_ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    Last tested {new Date(r.last_tested_at).toLocaleString()}{!r.last_test_ok && r.last_test_error ? ` — ${r.last_test_error}` : ""}
                  </div>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => test(r.id)} disabled={testing === r.id} data-testid={`db-conn-test-${r.id}`} className="gap-1">
                {testing === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />} Test
              </Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`db-conn-edit-${r.id}`} className="gap-1">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDeleting({ id: r.id, name: r.name })} data-testid={`db-conn-delete-${r.id}`} className="gap-1 text-red-600 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New DB Connection" : "Edit DB Connection"}</DialogTitle>
            <DialogDescription>
              Encrypted at rest. Non-super-admins cannot view or edit this list.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="db-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.db_type} onValueChange={onTypeChange}>
                <SelectTrigger data-testid="db-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.db_types.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isDynamo && (
              <div className="space-y-1.5">
                <Label>Port</Label>
                <Input type="number" value={form.port} onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))} placeholder={String(dbTypeMeta(form.db_type).default_port || "")} data-testid="db-form-port" />
              </div>
            )}
            {isDynamo ? (
              <>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>AWS Region</Label>
                  <Input value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))} placeholder="us-east-1" data-testid="db-form-region" />
                </div>
                <div className="space-y-1.5">
                  <Label>Access Key ID</Label>
                  <Input value={form.aws_access_key_id} onChange={(e) => setForm((f) => ({ ...f, aws_access_key_id: e.target.value }))} data-testid="db-form-akid" />
                </div>
                <div className="space-y-1.5">
                  <Label>Secret Access Key {form.__has_aws_secret ? <span className="text-[10px] text-emerald-600 ml-1">(saved — leave blank to keep)</span> : null}</Label>
                  <Input type="password" value={form.aws_secret_access_key} onChange={(e) => setForm((f) => ({ ...f, aws_secret_access_key: e.target.value }))} data-testid="db-form-akse" />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Host</Label>
                  <Input value={form.host} onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))} placeholder="db.example.com" data-testid="db-form-host" />
                </div>
                <div className="space-y-1.5">
                  <Label>Username</Label>
                  <Input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} data-testid="db-form-username" />
                </div>
                <div className="space-y-1.5">
                  <Label>Password {form.__has_password ? <span className="text-[10px] text-emerald-600 ml-1">(saved — leave blank to keep)</span> : null}</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="db-form-password" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Database / Namespace</Label>
                  <Input value={form.database} onChange={(e) => setForm((f) => ({ ...f, database: e.target.value }))} data-testid="db-form-database" />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 sm:col-span-2">
                  <div><div className="text-sm font-medium text-slate-700">Use TLS/SSL</div><div className="text-[11px] text-slate-400">Require encrypted transport when connecting</div></div>
                  <Switch checked={!!form.ssl} onCheckedChange={(v) => setForm((f) => ({ ...f, ssl: v }))} data-testid="db-form-ssl" />
                </div>
              </>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="db-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="db-form-save" className="gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete DB Connection</DialogTitle>
            <DialogDescription>Delete <span className="font-medium text-slate-800">{deleting?.name}</span>? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button onClick={del} data-testid="db-conn-delete-confirm" className="bg-red-600 text-white hover:bg-red-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
